import { useEffect, useRef } from 'react';
import {
  Control,
  FieldArrayPath,
  FieldValues,
  useFieldArray,
} from 'react-hook-form';

import { Grid, IconButton, Stack, StackProps } from '@mui/material';

import { IcDeleteTrash } from '@/assets/images';
import { IRenderFormFieldProps, RenderFormField } from '@/components';
import { Button } from '@/components/TailwindComponents';
import { colors } from '@/theme/variables';

interface InputCollectionProps extends StackProps {
  name: FieldArrayPath<FieldValues>;
  control: Control<FieldValues, any>;
  addLabel: string;
  InputFields: IRenderFormFieldProps[];
  populateByDefault?: boolean;
  maxNewItems?: number;
  onRemoveLast?: () => void;
}
export const InputCollection = ({
  name,
  control,
  addLabel,
  InputFields,
  populateByDefault,
  maxNewItems = undefined,
  onRemoveLast,
  ...rest
}: InputCollectionProps) => {
  const { remove, append, fields } = useFieldArray({
    control,
    name,
  });

  const handleAddInput = () => {
    append(
      InputFields.reduce((acc, item) => {
        return { ...acc, [item.name]: '' };
      }, {}),
    );
  };
  const alreadyInitialized = useRef(false);

  useEffect(() => {
    if (!alreadyInitialized.current && populateByDefault && !fields.length) {
      alreadyInitialized.current = true;
      handleAddInput();
    }
  }, []);

  const showAddButton = !(maxNewItems && maxNewItems <= fields?.length);

  return (
    <Stack mx={1} justifyContent="center" {...rest}>
      {fields.map((inputField, index: number) => (
        <Grid
          container
          columnSpacing={1}
          key={`row-${inputField.id}`}
          alignItems="center"
        >
          {InputFields.map((field) => (
            <Grid
              key={`${field.name}-${inputField.id}`}
              item
              flex="1"
              maxWidth="45%"
            >
              <RenderFormField
                {...field}
                name={`${name}.${index}.${field.name}`}
              />
            </Grid>
          ))}
          <Grid mt={1} item>
            <IconButton
              onClick={() => {
                if (fields.length === 1 && onRemoveLast) {
                  onRemoveLast();
                } else {
                  remove(index);
                }
              }}
              sx={{ color: colors.error.main, mt: 2, alignSelf: 'center' }}
            >
              <IcDeleteTrash />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      {showAddButton && fields.length > 0 && (
        <Button
          type="button"
          variant="link"
          className="w-fit text-sm p-0 h-auto mt-2 no-underline hover:no-underline"
          style={{ color: colors.error.main }}
          onClick={handleAddInput}
        >
          + {addLabel}
        </Button>
      )}
    </Stack>
  );
};
